/**
 * How a figure is written down, before anything decides where to put it.
 *
 * Three rules live here, shared by every portal, because they are the ones a
 * screen gets wrong quietly:
 *
 *   §1.3a  an amount is minor units in a string, and never becomes a float
 *   §39.4  a figure that may still move says so, wherever it appears
 *   §39.3a a report says how old it is, in the form the reader is asking in
 *
 * No framework and no DOM. These rules are about wording and arithmetic, and a
 * test that has to mount a component to check whether the word "provisional"
 * survived is a test nobody maintains.
 */

import {
  dateFieldOrder,
  decimalSeparator,
  displayLocale,
  groupDigits,
  type Locale,
} from './i18n.ts';
import { spreadsheetSafe } from '@affdice/rules/spreadsheet-cell';

import { DISPLAY_CURRENCY_ORIGINAL, currencyExponent, displayCurrency } from './currencies.ts';
import { kitText } from './messages.ts';
import { stripRuleReferences } from './strip-rule-references.ts';
import type { ConversionNote, FxRateNote, FxRateTimingPolicy } from './client.ts';

export interface MoneyView {
  /**
   * null means the platform does not have this figure.
   *
   * Distinct from '0', which means it has it and it is nothing. The reports
   * returned a literal '0' for deposits and NGR because the projection that
   * produces them does not exist yet — and an affiliate reads "£0.00" as "your
   * traffic earned nothing", never as "not built". One of those starts an
   * argument with an account manager.
   */
  readonly amount_minor: string | null;
  readonly currency: string;
}

/** What an absent figure looks like. Not a zero, and never blank. */
export const UNAVAILABLE = '—';

/**
 * §1.2 / §1.3a — minor units arrive as a string because JSON numbers cannot
 * hold 64-bit integers. Formatted without ever becoming a float.
 *
 * §2.8c — and formatted for the reader: `1.234,56 EUR` for an operator reading
 * in Spanish, `1,234.56 EUR` for one reading in English, off the same
 * `amount_minor` string, which is what goes on the wire either way. The digits
 * are grouped by `Intl` from a `BigInt` and the fraction is sliced off the
 * original string, so nothing here is ever a float.
 *
 * The currency stays an ISO code and stays after the amount in every language.
 * §2.8b: it is an identifier, and moving where an identifier sits between one
 * screen and the next buys nothing a reader wanted.
 *
 * `locale` defaults to the tab's display locale rather than being passed down
 * — see `setDisplayLocale`. Pass it explicitly when rendering for somebody
 * other than the person reading.
 *
 * §1.3d / §21.7 — and so does `exponent`, from the currency itself. It used to
 * default to 2, which is right for most currencies and a hundred-fold error for
 * the ones with no minor unit: `ref_currencies.minor_unit_scale` is real (JPY is
 * 0), editable from the AMS console and honoured by `packages/money`, and it
 * never left the server. Not one of the eighty-four call sites here passed the
 * parameter, which is the argument for defaulting it rather than for asking
 * them again — see `setCurrencyScales`. An explicit exponent still wins, for
 * the server-composed case the parameter was added for.
 */
export function formatMoney(
  m: MoneyView,
  exponent = currencyExponent(m.currency),
  locale: Locale = displayLocale(),
): string {
  // Handled here rather than at each call site, because the one call site that
  // forgets is the one that renders a missing figure as zero.
  if (m.amount_minor === null || m.amount_minor === undefined) return UNAVAILABLE;

  const negative = m.amount_minor.startsWith('-');
  const digits = (negative ? m.amount_minor.slice(1) : m.amount_minor).padStart(exponent + 1, '0');
  const whole = digits.slice(0, digits.length - exponent);
  const fraction =
    exponent === 0
      ? ''
      : `${decimalSeparator(locale)}${digits.slice(digits.length - exponent)}`;
  const grouped = groupDigits(whole, locale);
  return `${negative ? '−' : ''}${grouped}${fraction} ${m.currency}`;
}

/**
 * A formatted amount and its currency code, as two parts, for a figure that
 * sets the code smaller than the digits.
 *
 * Read off the string `formatMoney` already made rather than formatted twice:
 * the code is always its last word, after one space, and splitting that word
 * off is the whole job. Anything else — a count, a percentage, UNAVAILABLE —
 * comes back whole with no unit, so a figure that is not money is untouched.
 */
export function splitCurrency(value: string): { amount: string; unit: string | null } {
  const m = /^(.*\S) ([A-Z]{3})$/.exec(value);
  return m ? { amount: m[1]!, unit: m[2]! } : { amount: value, unit: null };
}

/**
 * §2.8c — a count, grouped for the reader.
 *
 * Not money: no currency, no minor units, no rounding, and therefore none of
 * the reasons `formatMoney` is confined to the view-model layer. A `.tsx` may
 * call this, and most of the callers are exactly that — a `<td className="num">`
 * with a click count in it.
 *
 * It exists because those call sites each wrote `.toLocaleString('en-US')`,
 * forty-seven of them, which pins American grouping to every reader in every
 * language. `1,234,567` reads as three numbers to somebody whose language
 * groups with a full stop. One function, so the next one added moves with the
 * locale rather than against it.
 */
export function formatCount(n: number, locale: Locale = displayLocale()): string {
  if (!Number.isFinite(n)) return UNAVAILABLE;
  return groupDigits(String(Math.trunc(Math.abs(n))), locale).replace(/^/, n < 0 ? '−' : '');
}

/**
 * §39.4 — "Figures are labelled provisional everywhere… until the period is
 * **locked**, not merely closed."
 *
 * The label goes on the figure rather than at the top of the page. A banner is
 * read once and then scrolled past; an affiliate exporting three numbers into a
 * spreadsheet takes the numbers and leaves the banner behind.
 */
export interface FigureView {
  readonly label: string;
  readonly value: string;
  readonly provisional: boolean;
  readonly note?: string;
  /**
   * §21.7b — set when this figure was converted for display, and carrying the
   * rate, its source, its `as_of` and the rate policy that produced it. On the
   * figure rather than on the screen for the same reason `provisional` is: the
   * reader who copies one number out of a table takes the number, and a banner
   * at the top of the page does not travel with it.
   */
  readonly conversion?: ConversionProvenance;
}

export function figure(
  label: string,
  value: string,
  envelope: { is_provisional: boolean; provisional_because?: string },
  conversion?: ConversionProvenance | undefined,
): FigureView {
  return {
    label,
    value,
    provisional: envelope.is_provisional,
    ...(envelope.is_provisional
      ? {
          note: stripRuleReferences(
            envelope.provisional_because ?? kitText('figure.provisional'),
          ),
        }
      : {}),
    ...(conversion ? { conversion } : {}),
  };
}

/**
 * §39.3a — "Every report MUST display its data-as-of timestamp. An affiliate
 * refreshing a stale dashboard and seeing no change concludes the platform is
 * broken."
 *
 * Rendered as an age rather than a timestamp, because "3 minutes ago" answers
 * the question the affiliate is actually asking and an ISO string does not.
 */
export function freshness(dataAsOf: string, now: Date): string {
  const seconds = Math.max(0, Math.floor((now.getTime() - new Date(dataAsOf).getTime()) / 1000));
  if (seconds < 60) return kitText('freshness.now');
  if (seconds < 3600) {
    const n = Math.floor(seconds / 60);
    return kitText(n === 1 ? 'freshness.minutes.one' : 'freshness.minutes.other', { n });
  }
  if (seconds < 86_400) {
    const n = Math.floor(seconds / 3600);
    return kitText(n === 1 ? 'freshness.hours.one' : 'freshness.hours.other', { n });
  }
  const n = Math.floor(seconds / 86_400);
  return kitText(n === 1 ? 'freshness.days.one' : 'freshness.days.other', { n });
}

/**
 * Item 11 — stored in UTC everywhere (already true), shown in whichever
 * timezone the viewing device is actually set to. No explicit `timeZone`
 * option below is the whole mechanism: `Intl.DateTimeFormat` defaults to the
 * runtime's own zone, so the same instant reads as a different clock time on
 * an affiliate in Manila and an operator in London, and neither has to pick
 * anything for that to happen.
 *
 * Distinct from the accounting timezone (§22.2), which decides which
 * *calendar day* a transaction belongs to for period boundaries — UTC for
 * every operator since 2026-10-12. This is purely about how a timestamp is
 * written on screen, decided by the device reading it.
 *
 * Both take a full ISO instant, never a date-only string: a date-only value
 * has already lost the information a local-time conversion needs, and no
 * formatting choice here can recover it. A `DATE` column is not that case —
 * it never held an instant to lose — and it has its own function, `formatDay`,
 * a few lines down.
 */
/*
 * One date *shape* across all three consoles — dash-separated, two-digit day
 * and month, four-digit year — in the reader's own *order*.
 *
 * Everything is stored UTC (§22.2); this is the only place it becomes a
 * reading, and it renders in the *device's* zone so two people looking at the
 * same record see their own clock rather than the server's.
 *
 * The order used to be pinned to MM-DD-YYYY for everybody, so that the same
 * timestamp did not arrive in a different order for every reader. That was
 * right while the reader's order came from whatever their device happened to
 * be set to, and wrong once they choose a language: §2.8c is explicit that
 * dates are formatted per locale, and `04-03-2026` on a Brazilian operator's
 * screen is not merely foreign, it is a different day. So the order now
 * follows the locale — one the reader picked and can change — while the
 * separator and the field widths stay fixed, which is the part of "one format"
 * that was actually protecting anything. English output is unchanged.
 *
 * `formatToParts` rather than a format string: it is what converts to the
 * device zone correctly, including the day rolling over across it, and it is
 * also what reports the locale's field order rather than us tabulating it.
 */
function parts(d: Date, locale: Locale): Record<string, string> {
  const found: Record<string, string> = {};
  let format: Intl.DateTimeFormat;
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    // §39.3a's cousin: two staff in different countries read the same
    // instant as the same wall-clock string once each has converted it to
    // their own device zone, and neither knows the other did. `formatDate`
    // never needs this — a bare date carries no zone to be ambiguous about —
    // so it is added here and only `formatDateTime` reads it back out.
    timeZoneName: 'short',
  };
  try {
    // No `hour12`: the locale decides. English keeps `02:30 PM`; Spanish and
    // Portuguese get `14:30`, which is what those readers write.
    format = new Intl.DateTimeFormat(locale, options);
  } catch {
    format = new Intl.DateTimeFormat('en', options);
  }
  for (const p of format.formatToParts(d)) found[p.type] = p.value;
  return found;
}

export function formatDate(iso: string, locale: Locale = displayLocale()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return UNAVAILABLE;
  const p = parts(d, locale);
  return dateFieldOrder(locale)
    .map((field) => p[field] ?? '')
    .join('-');
}

/**
 * §22.1 / §22.4 — a calendar day the server already resolved, printed exactly
 * as it arrived, with no zone conversion anywhere in the path.
 *
 * `formatDate` above is for an *instant*, and converting one to the viewer's
 * device zone is correct by construction — the same moment is a different
 * wall-clock time in Manila and in London, and showing that is the point.
 * A `DATE` column (`period_start`, `paid_on`, `cohort_month`, a registration
 * day…) is not an instant: the platform already decided which calendar day it
 * is, in the operator's accounting timezone (§22.2), before the value ever
 * left the server. Running it through `formatDate` sends `2026-03-01` through
 * `new Date('2026-03-01')` — midnight UTC — and then to the device zone, and
 * an affiliate five and a half hours west of UTC is shown `02-28-2026` for a
 * period that started on the first: a day that appears nowhere in the actual
 * record.
 *
 * So this never constructs a `Date` and never asks `Intl` to convert a zone:
 * the three fields are read off the string directly and handed to the same
 * `dateFieldOrder` that orders them for `formatDate`, so the two render the
 * same shape in the same locale — one converts, one does not.
 */
export function formatDay(isoDate: string, locale: Locale = displayLocale()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return UNAVAILABLE;
  const found: Record<string, string> = { year: m[1]!, month: m[2]!, day: m[3]! };
  return dateFieldOrder(locale)
    .map((field) => found[field] ?? '')
    .join('-');
}

export function formatDateTime(iso: string, locale: Locale = displayLocale()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return UNAVAILABLE;
  const p = parts(d, locale);
  // A 24-hour locale reports no dayPeriod, and appending `undefined` to a
  // timestamp is how that gets shipped.
  const suffix = p['dayPeriod'] === undefined ? '' : ` ${p['dayPeriod']}`;
  // Same reasoning as the suffix above: absent rather than "undefined" if a
  // runtime's `Intl` ever declines to report one.
  const zone = p['timeZoneName'] === undefined ? '' : ` ${p['timeZoneName']}`;
  return `${formatDate(iso, locale)} ${p['hour']}:${p['minute']}${suffix}${zone}`;
}

/* ------------------------------------------------------------------ *
 * §21.7 / §21.7b — a converted figure, and what the reader is shown of it
 *
 * §21.7b asks for four things beside a converted figure: the rate, the rate's
 * source, its `as_of`, and the rate policy. Written out literally that is a
 * sentence per figure, and a report is not one figure — the operator dashboard
 * has thirteen cards and a breakdown table has as many rows as there are
 * campaigns. Forty repetitions of one sentence is how a caveat becomes
 * wallpaper, which is the failure `sharedNote` already exists to avoid for the
 * provisional label.
 *
 * So the provenance is deduplicated by what it actually says. Forty figures
 * converted at one rate produce one note, said once; two rates produce two
 * notes and a marker on each figure pointing at its own. The mark beside the
 * figure never claims a single rate when there was not one: a `MIXED` policy —
 * some rows frozen at a lock, some at the latest published rate — says so on
 * the figure itself, because "Converted" over one rate line would tell the
 * reader that rate produced this number, and it did not.
 * ------------------------------------------------------------------ */

/** One rate, with everything §21.7b requires shown of it. */
export interface FxRateLine {
  readonly from: string;
  readonly to: string;
  /** The ratio as the server rendered it. Never recomputed here (§1.3a). */
  readonly rate: string;
  readonly source: string;
  readonly asOf: string;
  readonly policy: string;
  /** The four of them in one line, which is what goes on the screen. */
  readonly sentence: string;
}

/**
 * The provenance of a converted figure — and of every other figure on the
 * screen that was converted the same way.
 */
export interface ConversionProvenance {
  /** Two figures converted identically share this, and so share one note. */
  readonly key: string;
  readonly currency: string;
  /** More than one rate, or more than one kind of rate, behind one figure. */
  readonly mixed: boolean;
  /** Set only when the screen carries more than one distinct provenance. */
  readonly marker?: string;
  /** Beside the figure. Short, and never implies one rate where there is not one. */
  readonly mark: string;
  /** The sentence under the table or the card group. */
  readonly summary: string;
  /**
   * §21.7b — the rate policy the operator configured, where the server sent
   * it. Beside `summary` rather than folded into it, because the two say
   * different things and a settled period legitimately disagrees with the
   * setting.
   */
  readonly timing?: string;
  readonly rates: readonly FxRateLine[];
  /** Which fields of the report this covers, in the order they arrived. */
  readonly figures: readonly string[];
}

export interface ConversionsView {
  /** What everything was converted to. Empty when the report mixed targets. */
  readonly currency: string;
  readonly notes: readonly ConversionProvenance[];
  /** Set when every converted figure shares one provenance: say it once. */
  readonly shared?: ConversionProvenance;
}

function policyWords(policy: string): string {
  switch (policy) {
    case 'FROZEN': return kitText('converted.policy.frozen');
    case 'LATEST': return kitText('converted.policy.latest');
    case 'AVERAGE': return kitText('converted.policy.average');
    default: return kitText('converted.policy.identity');
  }
}

/**
 * §21.7b — what the operator's own setting asks for, said beside what this
 * figure actually used.
 *
 * Absent when the server did not send it, rather than guessed at: a sentence
 * claiming the operator configured something is worse than no sentence.
 */
function timingWords(timing: FxRateTimingPolicy | undefined): string | undefined {
  switch (timing) {
    case 'TRANSACTION_TIME': return kitText('converted.timing.transaction');
    case 'PERIOD_END': return kitText('converted.timing.periodEnd');
    case 'PERIOD_AVERAGE': return kitText('converted.timing.periodAverage');
    default: return undefined;
  }
}

function sourceKindWords(kind: string | null): string | null {
  switch (kind) {
    case 'BRAND': return kitText('converted.kind.brand');
    case 'OPERATOR_PROVIDER': return kitText('converted.kind.operator');
    case 'PLATFORM_DEFAULT': return kitText('converted.kind.platform');
    case 'IDENTITY': return kitText('converted.kind.identity');
    // null is the frozen case: the freeze does not keep §21.3b's provenance,
    // which is a gap in what was stored rather than something to invent a
    // word for here.
    default: return null;
  }
}

function rateLine(r: FxRateNote): FxRateLine {
  const policy = policyWords(r.policy);
  const asOf = formatDateTime(r.as_of);
  // The source is a publisher's name and arrives from the server untranslated,
  // for the reason `figure.provisional`'s comment gives: a name a reader cannot
  // match to the institution that published it is worse than an English one.
  const source = stripRuleReferences(r.source);
  const kind = sourceKindWords(r.source_kind);
  const params = { from: r.from_currency, to: r.to_currency, rate: r.rate, source, asOf, policy };
  return {
    from: r.from_currency,
    to: r.to_currency,
    rate: r.rate,
    source,
    asOf,
    policy,
    sentence: kind
      ? kitText('converted.rate.line.kind', { ...params, kind })
      : kitText('converted.rate.line', params),
  };
}

/**
 * What identifies one provenance. Two figures converted from the same
 * currencies, at the same rates, from the same sources, as of the same
 * instants, under the same policy, have nothing different to tell the reader —
 * so they get one note between them rather than one each.
 */
function provenanceKey(note: ConversionNote): string {
  const rates = note.rates
    .map((r) =>
      [r.from_currency, r.rate_numerator, r.rate_denominator, r.source, r.source_kind ?? '',
        r.as_of, r.policy].join('~'),
    )
    .sort()
    .join(';');
  return `${note.to_currency}|${note.policy}|${note.rate_timing ?? ''}|${rates}`;
}

/**
 * Group a report's conversion notes into the smallest set of things the
 * reader has to read.
 *
 * Returns undefined when nothing was converted — which is the ordinary case,
 * because the platform default is to convert nothing at all. A screen that
 * says "no conversion was applied" on every report for every operator who
 * never asked for one is noise, and noise is what teaches people to skip the
 * line that matters.
 */
export function conversions(envelope: {
  readonly conversions?: readonly ConversionNote[];
}): ConversionsView | undefined {
  const raw = envelope.conversions;
  if (!raw || raw.length === 0) return undefined;

  const order: string[] = [];
  const grouped = new Map<string, { note: ConversionNote; figures: string[] }>();
  for (const note of raw) {
    const key = provenanceKey(note);
    const existing = grouped.get(key);
    if (existing) existing.figures.push(note.figure);
    else {
      order.push(key);
      grouped.set(key, { note, figures: [note.figure] });
    }
  }

  const many = order.length > 1;
  const notes = order.map((key, i) => {
    const { note, figures } = grouped.get(key)!;
    const rates = note.rates.map(rateLine);
    const marker = many ? String(i + 1) : undefined;
    // §21.7b — two different things both mean "not one rate", and the reader
    // needs to be told either way: rows converted under different policies
    // (MIXED), and one figure summed from several source currencies.
    const mixedPolicy = note.policy === 'MIXED';
    const mixed = mixedPolicy || rates.length > 1;
    const identity = note.policy === 'IDENTITY' && rates.length <= 1;

    const mark = identity
      ? kitText('converted.mark.identity')
      : mixed
        ? marker
          ? kitText('converted.mark.mixed.numbered', { marker })
          : kitText('converted.mark.mixed')
        : marker
          ? kitText('converted.mark.numbered', { marker })
          : kitText('converted.mark');

    const summary = mixedPolicy
      ? kitText('converted.summary.mixed', { currency: note.to_currency })
      : rates.length > 1
        ? kitText('converted.summary.several', {
            currency: note.to_currency,
            n: rates.length,
          })
        : note.policy === 'FROZEN'
          ? kitText('converted.summary.frozen', { currency: note.to_currency })
          : note.policy === 'AVERAGE'
            ? kitText('converted.summary.average', { currency: note.to_currency })
            : note.policy === 'IDENTITY'
              ? kitText('converted.summary.identity', { currency: note.to_currency })
              : kitText('converted.summary.latest', { currency: note.to_currency });

    const timing = timingWords(note.rate_timing);

    return {
      key,
      currency: note.to_currency,
      mixed,
      ...(marker ? { marker } : {}),
      mark,
      summary,
      ...(timing ? { timing } : {}),
      rates,
      figures: figures as readonly string[],
    } satisfies ConversionProvenance;
  });

  const targets = new Set(notes.map((n) => n.currency));
  return {
    currency: targets.size === 1 ? (notes[0]?.currency ?? '') : '',
    notes,
    ...(notes.length === 1 && notes[0] ? { shared: notes[0] } : {}),
  };
}

/** The provenance of one field of `data`, to hang on that field's figure. */
export function conversionOf(
  view: ConversionsView | undefined,
  figureField: string,
): ConversionProvenance | undefined {
  if (!view) return undefined;
  return view.notes.find((n) => n.figures.includes(figureField));
}

/* ------------------------------------------------------------------ *
 * §21.7 — which of the two views the reader is in, and the way out of it
 * ------------------------------------------------------------------ */

/** `CONVERTED` is the reader's display currency; `ORIGINAL` is as computed. */
export type CurrencyView = 'CONVERTED' | 'ORIGINAL';

export interface CurrencyViewControl {
  readonly view: CurrencyView;
  /** The currency the converted view uses. Never `ORIGINAL`. */
  readonly currency: string;
  /** Which of the two the reader is looking at, said rather than implied. */
  readonly showing: string;
  /** The label on the control that switches. Absent when there is nothing to switch to. */
  readonly action?: string;
  /** Where that control goes. Absent with `action`. */
  readonly toView?: CurrencyView;
  /** Whose choice this currency was, when it was not this reader's (E37's shape). */
  readonly inheritedNote?: string;
  /** That the switch is this screen only, said only where the switch exists. */
  readonly scopeNote?: string;
}

/**
 * §21.7 — "MUST offer the original-currency view."
 *
 * Offered, and only where there is one to offer. Three states, and the
 * difference between them is the whole point:
 *
 *   - The reader's effective currency is `ORIGINAL`. Nothing is converted,
 *     there is no second view, and this returns nothing at all rather than a
 *     control that does nothing — E37's correction to the language picker,
 *     which used to offer "use the operator default" from a state it could not
 *     return to.
 *   - Converted, with something converted. The switch back is offered.
 *   - Converted, with nothing converted — every figure was already in the
 *     target, or one of them is a blend §21.7e refuses to convert. Said, not
 *     silent, and no switch: the two views are the same screen.
 *   - Converted, where the reader's currency moved nothing but something was
 *     still converted — an operator-wide total over brands in more than one
 *     currency is added up in one, and the reader's currency is that one.
 *     The same screen again, so the same answer: said, and no switch.
 *
 * The original view of such a total is not free of conversion either, and
 * says so rather than claiming every figure is in the currency it was
 * computed in above a converted mark and a rate.
 *
 * `chosen` is the reader's own preference and null when they have never made
 * one, exactly as the locale is. A currency they are following rather than one
 * they picked is named as such.
 */
export function currencyViewControl(input: {
  readonly view: CurrencyView;
  readonly chosen: string | null;
  readonly operatorDefault: string;
  readonly conversions?: ConversionsView | undefined;
  /**
   * The report's own `converted_for_reader`. Only `false` changes anything:
   * absent is a server that did not say, and keeps the answer it had.
   */
  readonly convertedForReader?: boolean | undefined;
}): CurrencyViewControl | undefined {
  const effective = input.chosen ?? input.operatorDefault;
  if (!effective || effective === DISPLAY_CURRENCY_ORIGINAL) return undefined;

  const inheritedNote =
    input.chosen === null
      ? kitText('currencyView.inherited', { currency: effective })
      : undefined;

  if (input.view === 'ORIGINAL') {
    const addedUpIn = input.conversions?.notes[0]?.currency;
    return {
      view: 'ORIGINAL',
      currency: effective,
      showing: addedUpIn
        ? kitText('currencyView.showing.originalAddedUp', { currency: addedUpIn })
        : kitText('currencyView.showing.original'),
      action: kitText('currencyView.action.converted', { currency: effective }),
      toView: 'CONVERTED',
      ...(inheritedNote ? { inheritedNote } : {}),
      scopeNote: kitText('currencyView.temporary'),
    };
  }

  if (!input.conversions || input.conversions.notes.length === 0) {
    return {
      view: 'CONVERTED',
      currency: effective,
      showing: kitText('currencyView.showing.nothing', { currency: effective }),
      ...(inheritedNote ? { inheritedNote } : {}),
    };
  }

  if (input.convertedForReader === false) {
    return {
      view: 'CONVERTED',
      currency: effective,
      showing: kitText('currencyView.showing.addedUp', { currency: effective }),
      ...(inheritedNote ? { inheritedNote } : {}),
    };
  }

  return {
    view: 'CONVERTED',
    currency: effective,
    showing: kitText('currencyView.showing.converted', { currency: effective }),
    action: kitText('currencyView.action.original'),
    toView: 'ORIGINAL',
    ...(inheritedNote ? { inheritedNote } : {}),
    scopeNote: kitText('currencyView.temporary'),
  };
}

/* ------------------------------------------------------------------ *
 * §21.7f — a billing document is never restated
 * ------------------------------------------------------------------ */

/**
 * What a statement or an invoice says about the reader's display currency.
 *
 * The rule is not implemented by this sentence — it is implemented by the
 * statement and invoice view models formatting from the document's own stored
 * `currency` and from nothing else, which is what they have always done and
 * what the tests beside them now hold. The sentence exists because a reader
 * who has just set every report to euros and finds a statement in pounds has
 * no way to tell a rule from a bug, and the two look identical on screen.
 *
 * Returns nothing for a reader who is not converting anything, which is most
 * of them: the platform default converts nothing at all, and a note answering
 * a question nobody asked is the kind of line that teaches people to stop
 * reading the notes.
 */
export function billingCurrencyNote(
  input: { readonly chosen: string | null; readonly operatorDefault: string } = displayCurrency(),
): string | undefined {
  const effective = input.chosen ?? input.operatorDefault;
  if (!effective || effective === DISPLAY_CURRENCY_ORIGINAL) return undefined;
  return kitText('billing.notConverted', { currency: effective });
}

/**
 * A table on screen, as a file.
 *
 * Generic over the rows rather than written per report, because the reference
 * console offers the same two formats on every one of its reports and writing
 * a bespoke writer per panel is how three of them end up subtly disagreeing
 * about quoting.
 *
 * §39.9a — "exports respect the same permission boundaries as the UI. Export
 * is the most common accidental data-leak path in reporting systems." That
 * holds here by construction: this takes the rows the table is already
 * rendering, so it cannot select a column the screen did not.
 */
export function tableCsv(
  title: string,
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  return [
    `# ${title}`,
    headers.join(','),
    ...rows.map((r) => r.map(csvCell).join(',')),
  ].join('\n');
}

/**
 * One quoted CSV cell, guarded against running as a formula when the file is
 * opened: the same guard the server's report export applies (spreadsheetSafe).
 * The rows are what the screen shows, and a sub-ID or a referrer on screen is
 * text an affiliate or a visitor chose.
 */
export function csvCell(c: string): string {
  return `"${spreadsheetSafe(c).replace(/"/g, '""')}"`;
}

/**
 * One escaped attribute, name and value together.
 *
 * Built through a helper rather than spelled inline, for the reason the
 * affiliate console's copy of this writer is: `title="${...}"` in a template
 * reads as a JSX `title=` prop to the console-text check, which then asks for
 * an XML attribute name to be translated into Spanish.
 */
const XML_QUOTE = '"';
function xmlAttr(name: string, value: string): string {
  return `${name}=${XML_QUOTE}${xmlEscape(value)}${XML_QUOTE}`;
}

/** XML's five, so a value containing one cannot end a tag early. */
function xmlEscape(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * The same table as XML.
 *
 * Element names are derived from the *column keys*, never from the headers on
 * screen: a feed whose tags change when the reader switches the console to
 * Spanish is a feed that breaks on a language setting.
 */
export function tableXml(
  title: string,
  keys: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<report ${xmlAttr('title', title)}>`,
    ...rows.map((r) => [
      '  <row>',
      ...keys.map((k, i) => `    <${k}>${xmlEscape(r[i] ?? '')}</${k}>`),
      '  </row>',
    ].join('\n')),
    '</report>',
  ].join('\n');
}

/**
 * The same table as a spreadsheet file (.xls).
 *
 * SpreadsheetML — the XML workbook format the common spreadsheet programs read —
 * written by hand rather than through a library, because nothing in the
 * workspace builds a workbook and this is one sheet of text and numbers. A
 * CSV opened in a spreadsheet re-guesses every column (a sub-ID of "0012"
 * loses its zeros, a date flips day and month); this states each cell's type.
 *
 * A cell is a Number only when the whole text is a plain decimal with no
 * leading zero, so an identifier that happens to be digits stays text.
 */
export function tableXls(
  title: string,
  headers: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  const numeric = /^-?(0|[1-9]\d*)(\.\d+)?$/;
  const cell = (value: string): string => (numeric.test(value)
    ? `<Cell><Data ss:Type="Number">${value}</Data></Cell>`
    : `<Cell><Data ss:Type="String">${xmlEscape(value)}</Data></Cell>`);
  const row = (values: readonly string[]): string => `   <Row>${values.map(cell).join('')}</Row>`;
  // A sheet name is at most 31 characters and may not hold []:*?/\
  const sheet = title.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31) || 'Report';
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<?mso-application progid="Excel.Sheet"?>',
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"',
    ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">',
    ` <Worksheet ${'ss:'}${xmlAttr('Name', sheet)}>`,
    '  <Table>',
    `   <Row>${headers.map((h) => `<Cell><Data ss:Type="String">${xmlEscape(h)}</Data></Cell>`).join('')}</Row>`,
    ...rows.map(row),
    '  </Table>',
    ' </Worksheet>',
    '</Workbook>',
  ].join('\n');
}

/* ------------------------------------------------------------------ *
 * Countries — a code is not a name
 * ------------------------------------------------------------------ */

/**
 * "NG" as "Nigeria", in the reader's own language.
 *
 * The geo reports showed the two-letter code the click was recorded with,
 * which is what the platform stores and not what anybody calls a country. The
 * reference console the owner compared against shows the name.
 *
 * `Intl.DisplayNames` rather than a table shipped with the app: the browser
 * already has every country in every locale we translate into, so a table here
 * would be a second copy to keep current and would only ever be in English.
 *
 * Falls back to the code. An unknown or malformed value is shown as it was
 * recorded rather than dropped — "XK" is more use to somebody investigating
 * than a blank, and the code is what the filters and the export match on.
 */
const countryNames = new Map<string, Intl.DisplayNames | null>();

export function countryName(code: string | null | undefined, locale: Locale = displayLocale()): string {
  const iso = (code ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(iso)) return code?.trim() ? code.trim() : UNAVAILABLE;
  if (!countryNames.has(locale)) {
    try {
      countryNames.set(locale, new Intl.DisplayNames([locale], { type: 'region' }));
    } catch {
      // An engine without the region data: the code is still the truth.
      countryNames.set(locale, null);
    }
  }
  try {
    return countryNames.get(locale)?.of(iso) ?? iso;
  } catch {
    return iso;
  }
}

/**
 * The flag for a two-letter country code, as the pair of regional indicator
 * symbols Unicode defines for it.
 *
 * Deliberately alongside the name and never instead of it (§8 — do not rely on
 * colour or an image alone): Windows ships no flag glyphs at all and renders
 * the pair as the two letters, which is why the name carries the meaning and
 * this is decoration in front of it.
 */
/**
 * A country as a reader sees it: flag, then name.
 *
 * `countryName` and `countryFlag` shipped together and then each call site
 * decided for itself whether to use them, so the geo pivot and one popup
 * showed "🇳🇬 Nigeria" while the click log, the players report and the
 * operator's acquisition column showed "NG" — the same fact in three
 * different presentations on one platform (owner, 2026-09-29: "country flag
 * ka disat nahit?").
 *
 * One function, so a country reads the same everywhere and a new screen gets
 * it right by using it rather than by remembering to.
 *
 * `absent` is what an unrecorded country shows — a dash on a table, a phrase
 * in a popup — because "no country" is a different thing from a country whose
 * name we happen not to know, and only the caller knows which reads better in
 * its own column.
 */
export function countryLabel(code: string | null | undefined, absent = '—'): string {
  const iso = (code ?? '').trim();
  if (!iso) return absent;
  const flag = countryFlag(iso);
  const name = countryName(iso);
  return flag ? `${flag} ${name}` : name;
}

/**
 * A report's `geo` key — `US`, or `US-NJ` where the subdivision was known —
 * as "🇺🇸 United States · NJ". The subdivision stays a code: the runtime
 * names countries, not their states, and a wrong expansion is worse than a
 * short one.
 */
export function geoLabel(value: string | null | undefined, absent = '—'): string {
  const key = (value ?? '').trim();
  if (!key) return absent;
  const [country, ...rest] = key.split('-');
  const label = countryLabel(country, absent);
  const subdivision = rest.join('-');
  return subdivision ? `${label} · ${subdivision}` : label;
}

export function countryFlag(code: string | null | undefined): string {
  const iso = (code ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(iso)) return '';
  return String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

const BROWSER_NAMES: Readonly<Record<string, string>> = {
  CHROME: 'Chrome', SAFARI: 'Safari', FIREFOX: 'Firefox', EDGE: 'Edge',
  OPERA: 'Opera', SAMSUNG: 'Samsung Internet',
};

/**
 * What a click was made on, as a reader says it: "Mac · Safari",
 * "iPhone · Chrome", "Windows · Edge".
 *
 * Built from what the edge recorded — operating system, device class and
 * browser. A user agent never says which Mac (MacBook or iMac), so a Mac is
 * "Mac"; an iPad asking for the desktop site says "Macintosh" and reads as one.
 * Falls back to the device class when the system is not one named here, and
 * leaves the browser off when it is unknown rather than printing "Other".
 */
export function deviceLabel(
  device: string | null | undefined,
  os: string | null | undefined,
  browser: string | null | undefined,
): string {
  const tablet = device === 'TABLET';
  const system =
    os === 'IOS' ? (tablet ? 'iPad' : 'iPhone')
    : os === 'MACOS' ? 'Mac'
    : os === 'WINDOWS' ? 'Windows'
    : os === 'ANDROID' ? 'Android'
    : os === 'CHROMEOS' ? 'Chromebook'
    : os === 'LINUX' ? 'Linux'
    : device ? device.charAt(0) + device.slice(1).toLowerCase()
    : null;
  const name = browser ? BROWSER_NAMES[browser] : undefined;
  if (system && name) return `${system} · ${name}`;
  return system ?? name ?? UNAVAILABLE;
}

/** The landing address the platform saves — with `{click_id}` — for a console's preview. */
export { withClickIdMacro } from '@affdice/rules/click-id-macro';
